<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import ConfirmDialog from "./shared/confirm-dialog.vue";
  import IconHint from "./icon-hint.vue";
  import LineItemsTable from "./line-items/line-items-table.vue";
  import ShipmentItemFinishedGoodCombobox from "./shipment-item-finished-good-combobox.vue";
  import ShipmentItemRow from "./shipment-item-row.vue";
  import WrapWithHint from "./wrap-with-hint.vue";
  import {
    autofillShipmentLotCodes,
    createShipmentItem,
    updateShipmentItem,
    deleteShipmentItem,
  } from "./api/shipment-items.js";
  import { GENERIC_ERROR } from "./line-items/constants.js";
  import { Flash } from "~/jose_modern/flash.js";
  import { quantityWithUnit } from "~/jose_modern/quantity_with_unit";
  import { openLotCodesShipmentShipmentItemsPath } from "~/routes.js";

  const props = defineProps({
    items: { type: Array, required: true },
    userId: { type: Number, required: true },
    shipmentId: { type: Number, required: true },
    lotSeeds: { type: Object, default: () => ({}) },
    requireLotCode: { type: Boolean, default: false },
  });

  const AUTOFILL_HINT =
    "If you've been tracking your lot codes in ReciPal, we can automatically fill them in " +
    "for each item. We'll use available lots you've drawn from most recently first, then " +
    "FIFO (first in, first out) for lots you haven't used yet. We won't draw from lot codes " +
    "with negative quantities";

  let tableItems = _ref(props.items);
  let tableLotSeeds = _ref(props.lotSeeds);
  let tableVersion = _ref(0);
  let savedCount = _ref(props.items.length);
  let hasUnsaved = _ref(false);
  let shippedLotIds = _ref([]);
  let autofilling = _ref(false);
  let showConfirmAutofill = _ref(false);

  const comboboxProps = _computed(() => ({ userId: props.userId }));

  const rowProps = _computed(() => ({
    lotSeeds: tableLotSeeds.value,
    lotCodesUrl: openLotCodesShipmentShipmentItemsPath(props.shipmentId),
    requireLotCode: props.requireLotCode,
    shippedLotIds: shippedLotIds.value,
  }));

  const api = {
    create: (attributes) => createShipmentItem(props.shipmentId, attributes),
    update: (id, attributes) => updateShipmentItem(props.shipmentId, id, attributes),
    remove: (id) => deleteShipmentItem(props.shipmentId, id),
  };

  const labels = {
    model: "Shipment item",
    removeTitle: "Remove Item",
    removeMessage: "Are you sure you want to remove this item from the shipment?",
  };

  const lotDuplicateKey = (item) => `${item.item_id}:${item.persisted_lot_code_id ?? ""}`;

  function onSavedCountChanged(count) {
    savedCount.value = count;
    document.dispatchEvent(new CustomEvent("shipment-items-changed", { detail: { count } }));
  }

  function onItemsChanged(items) {
    shippedLotIds.value = items.map((item) => item.persisted_lot_code_id).filter((id) => id != null);

    const missing = props.requireLotCode ? items.length - shippedLotIds.value.length : 0;

    document.dispatchEvent(
      new CustomEvent("shipment-items-missing-lot-codes", { detail: { missing } })
    );
  }

  function onUnsavedChanged(unsaved) {
    hasUnsaved.value = unsaved;
    document.dispatchEvent(
      new CustomEvent("shipment-items-has-unsaved", { detail: { hasUnsaved: unsaved } })
    );
  }

  const autofillDisabledHint = _computed(() => {
    if (savedCount.value === 0) return "Add items to the shipment before autofilling lots.";
    if (hasUnsaved.value) return "Save all items before autofilling lots.";

    return null;
  });

  function onAutofillClick() {
    if (shippedLotIds.value.length > 0) showConfirmAutofill.value = true;
    else autofillLots();
  }

  async function autofillLots() {
    autofilling.value = true;
    try {
      const { response, data } = await autofillShipmentLotCodes(props.shipmentId).catch(() => ({
        response: null,
        data: null,
      }));

      if (!response?.ok) {
        Flash.error(data?.errors?.join(", ") || GENERIC_ERROR);
        return;
      }

      tableItems.value = data.items;
      tableLotSeeds.value = data.lot_seeds;
      tableVersion.value += 1;

      if (data.unfilled_items.length) {
        Flash.error(
          `Could not autofill all lot codes: no open lots for ${data.unfilled_items.join(", ")}.`
        );
      } else {
        Flash.success("Lot codes autofilled successfully.");
      }
    } finally {
      autofilling.value = false;
    }
  }

  function groupTotal(items) {
    const byUnit = new Map();
    items.forEach((item) => {
      byUnit.set(item.unit, (byUnit.get(item.unit) ?? 0) + (Number(item.quantity) || 0));
    });

    return [...byUnit]
      .map(([unit, total]) => quantityWithUnit(Number(total.toFixed(6)), unit))
      .join(" + ");
  }

  function mapSelection(finishedGood) {
    return {
      itemId: finishedGood.id,
      name: finishedGood.recipe.name,
      customName: null,
      unit: finishedGood.unit,
      units: finishedGood.units,
    };
  }
</script>

<template>
  <div class="shipment-items">
    <LineItemsTable
      :key="tableVersion"
      :items="tableItems"
      :item-combobox="ShipmentItemFinishedGoodCombobox"
      :combobox-props="comboboxProps"
      :map-selection="mapSelection"
      :api="api"
      :labels="labels"
      :row="ShipmentItemRow"
      :row-props="rowProps"
      :exclude-added-items="false"
      :duplicate-key="lotDuplicateKey"
      :group-key="(item) => item.item_id"
      @saved-count-changed="onSavedCountChanged"
      @unsaved-changed="onUnsavedChanged"
      @items-changed="onItemsChanged"
    >
      <template #header>
        <div class="shipment-items-header">
          <div>Item</div>
          <div>Quantity</div>
          <div>Lot code</div>
          <div></div>
        </div>
      </template>
      <template #group-footer="{ groupItems }">
        <div v-if="groupItems.length > 1" class="shipment-items-total">
          <div>Total</div>
          <div>{{ groupTotal(groupItems) }}</div>
        </div>
      </template>
    </LineItemsTable>

    <div class="autofill-bar">
      <WrapWithHint v-if="autofillDisabledHint" tag="span" :content="autofillDisabledHint">
        <button class="button is-info is-outlined autofill-btn" disabled>
          <span class="icon"><i class="fa-sharp-duotone fa-wand-magic-sparkles"></i></span>
          <span>Autofill Lots</span>
          <IconHint :content="AUTOFILL_HINT" />
        </button>
      </WrapWithHint>
      <button
        v-else
        class="button is-info is-outlined autofill-btn"
        :class="{ 'is-loading': autofilling }"
        :disabled="autofilling"
        @click="onAutofillClick"
      >
        <span class="icon"><i class="fa-sharp-duotone fa-wand-magic-sparkles"></i></span>
        <span>Autofill Lots</span>
        <IconHint :content="AUTOFILL_HINT" @click.stop />
      </button>
    </div>

    <ConfirmDialog
      v-model="showConfirmAutofill"
      title="Autofill Lots"
      confirm-text="Continue"
      confirm-class="is-info"
      :is-loading="autofilling"
      @confirm="autofillLots"
    >
      There are already some lot codes populated.
      <b>Autofill Lots will clear all existing lot codes and replace them.</b><br />
      Continue?
    </ConfirmDialog>
  </div>
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";
  @import "./line-items/line-items";

  .shipment-items {
    --line-items-columns: minmax(240px, 1fr) 300px minmax(220px, 320px) 6rem;
  }

  .autofill-btn:not([disabled]):hover,
  .autofill-btn:not([disabled]):focus {
    :deep(.icon.has-text-info) {
      color: white !important;
    }
  }

  .autofill-bar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 0.75rem;

    @media screen and (max-width: $line-items-collapse) {
      :deep(.hint-wrapper:not(.icon)) {
        flex-grow: 1;
      }

      .autofill-btn {
        flex-grow: 1;
        width: 100%;
      }
    }
  }

  .shipment-items-total {
    @include line-items-columns;

    position: relative;
    font-weight: 600;
    border-bottom: 1px solid $border;
    margin-bottom: 0.5rem;

    @media screen and (min-width: $line-items-collapse + 1) {
      > div:first-child {
        padding-left: 2.25rem;
      }

      &::before,
      &::after {
        content: "";
        position: absolute;
        left: 1.25rem;
        background-color: $border;
      }

      &::before {
        top: 0;
        height: 50%;
        width: 2px;
      }

      &::after {
        top: 50%;
        width: 0.75rem;
        height: 2px;
      }
    }

    @media screen and (max-width: $line-items-collapse) {
      @include line-items-card-sides;
      @include line-items-card-bottom;

      display: flex;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.5rem 1rem 1rem;
      border-top: 1px solid $border-light;
    }
  }

  .shipment-items-header {
    @include line-items-columns;

    font-weight: bold;
    border-bottom: 2px solid $border;

    @media screen and (max-width: $line-items-collapse) {
      display: none;
    }
  }
</style>
